/**
 * Edits to the Mapbox Standard basemap: its look, the 3D and satellite switches,
 * and which place icons stay on.
 */
import type { FilterSpecification, Map as MapboxMap } from 'mapbox-gl';
import { STATION_MODEL_LAYER_IDS } from '../constants';
import {
  ALL_PLACES_VISIBLE,
  PLACE_SITE_TYPES,
  type PlaceVisibility,
} from '../constants/placeSiteTypes';
import { POI_LABEL_MAKI_NAMES } from './makiPaths';

/** Mapbox Standard. Trees and landmarks live here and stay hidden until 3D is on. */
export const STANDARD_STYLE_URL = 'mapbox://styles/mapbox/standard';

/**
 * Standard has no satellite switch. This sibling style is the same basemap
 * with imagery under the roads and labels.
 */
export const STANDARD_SATELLITE_STYLE_URL = 'mapbox://styles/mapbox/standard-satellite';

/**
 * Opening camera tilt. Steep enough that terrain and buildings read as 3D,
 * shallow enough that streets stay usable. The compass can still change it.
 */
export const MAP_3D_PITCH = 45;

/**
 * Standard, tuned to the light basemap: cool near-white land and white roads.
 * Place names, road names, and region borders stay a light gray so the stations read first.
 * Motorway badges are separate sprites; `quietRoadShields` grays those numbers.
 * 3D models stay off until the control turns them on.
 */
export const MAP_BASEMAP_CONFIG = {
  basemap: {
    theme: 'faded',
    lightPreset: 'day',
    show3dObjects: false,
    colorLand: '#f7f7f8',
    colorWater: '#dbdcdd',
    colorGreenspace: '#e6e8ec',
    colorCommercial: '#e6e8ec',
    colorEducation: '#e6e8ec',
    colorMedical: '#e6e8ec',
    colorIndustrial: '#e6e8ec',
    colorBuildings: '#e8e8e8',
    colorMotorways: '#ffffff',
    colorTrunks: '#ffffff',
    colorRoads: '#ffffff',
    colorHdRoads: '#ffffff',
    colorPlaceLabels: '#9a9b9e',
    colorRoadLabels: '#b0b1b4',
    colorAdminBoundaries: '#7d7e81',
  },
} as const;

const basemapRequest = new WeakMap<object, number>();
const imageryRequest = new WeakMap<object, number>();
const map3dEnabled = new WeakMap<object, boolean>();

/** Whether the 3D control currently has the station models and the basemap models on. */
export function isMap3dEnabled(map: object): boolean {
  return map3dEnabled.get(map) ?? false;
}

/** Pitch `fitBounds` must receive. Omitting it resets the camera to flat. */
export function keptMapPitch(map: { getPitch?: () => number }): number {
  return map.getPitch?.() ?? 0;
}

function beginBasemapChange(map: object): number {
  const id = (basemapRequest.get(map) ?? 0) + 1;
  basemapRequest.set(map, id);
  return id;
}

/**
 * Shows or hides Standard's 3D models and tilts the camera. The basemap stays
 * Standard either way. A newer toggle wins if the style is still loading.
 */
function setStationModelsVisible(map: MapboxMap, visible: boolean) {
  const visibility = visible ? 'visible' : 'none';
  for (const layerId of STATION_MODEL_LAYER_IDS) {
    if (!map.getLayer(layerId)) continue;
    map.setLayoutProperty(layerId, 'visibility', visibility);
  }
}

function applyMap3d(map: MapboxMap, enabled: boolean, duration: number): void {
  const id = beginBasemapChange(map);
  map3dEnabled.set(map, enabled);
  let applied = false;
  let waiting = false;
  const apply = () => {
    if (applied || basemapRequest.get(map) !== id) return;
    // Setting the basemap while the style is still settling throws, and the
    // load event may already have passed. Wait for whichever comes next.
    if (!map.isStyleLoaded()) {
      if (waiting) return;
      waiting = true;
      const retry = () => {
        waiting = false;
        apply();
      };
      map.once('style.load', retry);
      map.once('idle', retry);
      return;
    }
    applied = true;
    map.setConfigProperty('basemap', 'show3dObjects', enabled);
    setStationModelsVisible(map, enabled);
    map.easeTo({ pitch: enabled ? MAP_3D_PITCH : 0, duration });
  };
  apply();
}

/** Reveals Standard's 3D models and tilts. */
export function showMap3d(map: MapboxMap, duration: number): void {
  applyMap3d(map, true, duration);
}

/** Hides the 3D models and flattens. The basemap stays Standard. */
export function hideMap3d(map: MapboxMap, duration: number): void {
  applyMap3d(map, false, duration);
}

const ROAD_SHIELD_LAYERS = ['road-number-shield', 'road-exit-shield'] as const;

/**
 * Swaps the basemap import between Standard and Standard Satellite.
 * A raster drawn under Standard stays covered by the opaque land background.
 * Returns false while the style is still loading, so the caller can retry.
 * The 3D switch is put back once the new import has loaded. Place icons are
 * the shell's, which reapplies them on that same event.
 */
export function setBasemapSatellite(map: MapboxMap, satellite: boolean): boolean {
  if (!map.isStyleLoaded()) return false;
  const id = (imageryRequest.get(map) ?? 0) + 1;
  imageryRequest.set(map, id);
  map.updateImport('basemap', satellite ? STANDARD_SATELLITE_STYLE_URL : STANDARD_STYLE_URL);
  const apply = () => {
    if (imageryRequest.get(map) !== id) return;
    if (!quietRoadShields(map)) {
      map.once('idle', apply);
      return;
    }
    map.setConfigProperty('basemap', 'show3dObjects', isMap3dEnabled(map));
  };
  map.once('style.import.load', apply);
  return true;
}

/** Same quiet gray as the other names. The shield sprite itself has no color config. */
const ROAD_SHIELD_TEXT = '#7d7e81';

type BasemapStyle = {
  getOwnLayer(id: string): unknown;
  setFilter(layerId: string, filter: unknown): void;
  setLayoutProperty(layerId: string, name: string, value: unknown): void;
  setPaintProperty(layerId: string, name: string, value: unknown): void;
};

function basemapStyle(map: MapboxMap): BasemapStyle | undefined {
  const style = (
    map as MapboxMap & {
      style?: { getFragmentStyle?(id: string): BasemapStyle | undefined };
    }
  ).style;
  return style?.getFragmentStyle?.('basemap');
}

/** Fragment edits do not schedule a redraw on their own. */
function redrawStyle(map: MapboxMap): void {
  const redraw = (map as MapboxMap & { _update?(changed: boolean): void })._update;
  redraw?.call(map, true);
}

/**
 * Motorway and highway numbers are colored shield sprites. Scaling the sprite
 * away leaves the route number in gray. Returns false while the basemap import
 * is still missing, so the caller can retry.
 */
export function quietRoadShields(map: MapboxMap): boolean {
  const basemap = basemapStyle(map);
  if (!basemap?.getOwnLayer(ROAD_SHIELD_LAYERS[0])) return false;
  for (const layerId of ROAD_SHIELD_LAYERS) {
    if (!basemap.getOwnLayer(layerId)) continue;
    basemap.setLayoutProperty(layerId, 'icon-size', 0);
    basemap.setPaintProperty(layerId, 'icon-opacity', 0);
    basemap.setPaintProperty(layerId, 'text-color', ROAD_SHIELD_TEXT);
    basemap.setPaintProperty(layerId, 'text-halo-color', '#f7f7f8');
    basemap.setPaintProperty(layerId, 'text-halo-width', 1.4);
  }
  redrawStyle(map);
  return true;
}

const POI_LAYER = 'poi-label';
/** Civil and military airports. Not a poi-label icon, the way a bus stop is not one either. */
const AIRPORT_LAYER = 'airport-label';

/**
 * Basemap icons that stay on the map. Anything else stays off.
 * One icon can cover several Google types, so it is listed once, at its highest priority.
 * Parking stays with the restaurants: lots and garages share that icon with park-and-ride.
 */
export const P1_PLACE_MAKI = [
  'airport',
  'amusement-park',
  'campsite',
  'fast-food',
  'fuel',
  'grocery',
  'lodging',
  'shop',
  'terminal',
  'zoo',
] as const;

export const P2_PLACE_MAKI = [
  'aquarium',
  'bakery',
  'bowling-alley',
  'car',
  'casino',
  'cinema',
  'fitness-centre',
  'furniture',
  'golf',
  'parking',
  'parking-garage',
  'parking-paid',
  'restaurant',
  'restaurant-noodle',
  'restaurant-pizza',
  'restaurant-seafood',
  'stadium',
] as const;

export const P3_PLACE_MAKI = [
  'alcohol-shop',
  'car-rental',
  'car-repair',
  'harbor',
  'hardware',
  'hospital',
] as const;

const CURATED_PLACE_MAKI = [...P1_PLACE_MAKI, ...P2_PLACE_MAKI, ...P3_PLACE_MAKI];

const NAMED_PLACE_MAKI = [...new Set(PLACE_SITE_TYPES.flatMap((type) => [...type.makis]))];
const NAMED_PLACE_MAKI_SET = new Set<string>(NAMED_PLACE_MAKI);
/** Curated icons that are not a site-type chip. The Other chip owns them. */
const OTHER_CURATED_MAKI = CURATED_PLACE_MAKI.filter((maki) => !NAMED_PLACE_MAKI_SET.has(maki));

/** Street level. Icons outside the site-type chips stay off until the camera reaches it. */
export const ALL_PLACES_ZOOM = 18;

/**
 * Icon the layer paints. maki_beta wins when it is a real glyph, otherwise maki.
 * A bare marker is not an icon. Same rule as `paintedPlaceMaki`.
 */
function paintedMaki(): unknown[] {
  return [
    'case',
    ['match', ['get', 'maki_beta'], [...POI_LABEL_MAKI_NAMES], true, false],
    ['get', 'maki_beta'],
    ['all', ['to-boolean', ['get', 'maki']], ['!=', ['get', 'maki'], 'marker']],
    ['get', 'maki'],
    '',
  ];
}

function matchMaki(makis: readonly string[]): unknown[] {
  return ['match', paintedMaki(), makis.length === 1 ? makis[0] : [...makis], true, false];
}

/** Icons of the business types that are on. Types that share an icon contribute it once. */
function enabledMakis(places: PlaceVisibility): string[] {
  return [
    ...new Set(
      PLACE_SITE_TYPES.filter((type) => places[type.id]).flatMap((type) => [...type.makis]),
    ),
  ];
}

/** Icons the chips leave on at every zoom, including the Other chip's curated set. */
function shownMakis(places: PlaceVisibility): string[] {
  const named = enabledMakis(places);
  if (!places.other) return named;
  return [...named, ...OTHER_CURATED_MAKI];
}

/** A title the poi-label layer can draw. An icon with no name stays off. */
function hasPlaceLabel(): unknown[] {
  return ['any', ['to-boolean', ['get', 'name_en']], ['to-boolean', ['get', 'name']]];
}

/** A real icon. The bare marker is a dot with no type. */
function hasPlaceType(): unknown[] {
  return [
    'any',
    ['all', ['to-boolean', ['get', 'maki']], ['!=', ['get', 'maki'], 'marker']],
    ['all', ['to-boolean', ['get', 'maki_beta']], ['!=', ['get', 'maki_beta'], 'marker']],
  ];
}

/**
 * Site-type chips below street level. With Other on, zoom 18 still admits every
 * other icon Mapbox's density allows. A hidden site type stays off at that zoom
 * too. A place still needs a label and a type.
 */
export function placeIconGate(places: PlaceVisibility = ALL_PLACES_VISIBLE): unknown[] {
  return ['all', hasPlaceLabel(), hasPlaceType(), placeVisibilityClause(places)];
}

function placeVisibilityClause(places: PlaceVisibility): unknown {
  const clauses: unknown[] = [];
  const named = enabledMakis(places);
  if (named.length > 0) clauses.push(matchMaki(named));
  if (places.other) {
    if (OTHER_CURATED_MAKI.length > 0) clauses.push(matchMaki(OTHER_CURATED_MAKI));
    clauses.push(['all', ['>=', ['zoom'], ALL_PLACES_ZOOM], ['!', matchMaki(NAMED_PLACE_MAKI)]]);
  }
  if (clauses.length === 0) return false;
  if (clauses.length === 1) return clauses[0];
  return ['any', ...clauses];
}

/** Our gate, so a later chip change replaces it instead of stacking another. */
function isPlaceIconGate(clause: unknown): boolean {
  if (!Array.isArray(clause) || clause[0] !== 'all' || clause.length !== 4) return false;
  const text = JSON.stringify(clause);
  return text.includes('"name_en"') && text.includes('"maki_beta"');
}

function pointOfInterestFilter(existing: unknown, places: PlaceVisibility): unknown[] {
  const gate = placeIconGate(places);
  const clauses =
    Array.isArray(existing) && existing[0] === 'all'
      ? existing.slice(1).filter((clause) => !isPlaceIconGate(clause))
      : existing == null
        ? []
        : [existing];
  return ['all', ...clauses, gate];
}

/**
 * Keeps Mapbox's own zoom and density, and limits the layer to the site-type
 * chips that are on. Other categories stay off until zoom 18, and a hidden chip
 * stays off there too. A place without a label or a type stays off.
 * Airports are the airport-label layer, so the Airport chip sets that layer's
 * visibility. The poi-label filter never sees them.
 * Collision and the zoom-17 icon fade would hide a place the rank had already
 * admitted, so an admitted place stays drawn. Every title is one size, so a
 * low sizerank does not grow, and the size does not jump when zoom crosses 17.
 * Returns false while the basemap import is still missing, so the caller can retry.
 */
export function keepPointsOfInterest(
  map: MapboxMap,
  places: PlaceVisibility = ALL_PLACES_VISIBLE,
): boolean {
  const basemap = basemapStyle(map);
  const layer = basemap?.getOwnLayer(POI_LAYER) as { filter?: unknown } | undefined;
  if (!basemap || !layer) return false;
  basemap.setFilter(POI_LAYER, pointOfInterestFilter(layer.filter, places));
  basemap.setLayoutProperty(POI_LAYER, 'symbol-sort-key', ['get', 'filterrank']);
  basemap.setLayoutProperty(POI_LAYER, 'icon-allow-overlap', true);
  basemap.setLayoutProperty(POI_LAYER, 'text-allow-overlap', true);
  basemap.setLayoutProperty(POI_LAYER, 'icon-ignore-placement', true);
  basemap.setLayoutProperty(POI_LAYER, 'text-ignore-placement', true);
  basemap.setLayoutProperty(POI_LAYER, 'text-size', 13);
  basemap.setPaintProperty(POI_LAYER, 'icon-opacity', [
    'case',
    ['to-boolean', ['feature-state', 'hide']],
    0,
    1,
  ]);
  if (basemap.getOwnLayer(AIRPORT_LAYER)) {
    basemap.setLayoutProperty(AIRPORT_LAYER, 'visibility', places.airport ? 'visible' : 'none');
  }
  redrawStyle(map);
  return true;
}

const PLACE_HOLD_SOURCE = 'place-hold';
const PLACE_HOLD_LAYER = 'place-hold';
/** The streets tile still has a landmark here. The next zoom drops it. */
const PLACE_TILE_ZOOM = 15;

/**
 * Keeps a landmark whose name the high-zoom tile removes. Rungis is one:
 * it is in the zoom-15 tile and missing from zoom 16 on.
 * Returns false while the style is not ready, so the caller can retry.
 */
function holdPlaceFilter(places: PlaceVisibility): FilterSpecification {
  const makis = shownMakis(places);
  return [
    'all',
    makis.length > 0 ? matchMaki(makis) : false,
    hasPlaceLabel(),
    ['<', ['to-number', ['get', 'sizerank']], 5],
  ] as FilterSpecification;
}

export function holdPlacesTheTileDrops(
  map: MapboxMap,
  places: PlaceVisibility = ALL_PLACES_VISIBLE,
): boolean {
  if (!map.isStyleLoaded()) return false;
  const filter = holdPlaceFilter(places);
  if (!map.getSource(PLACE_HOLD_SOURCE)) {
    map.addSource(PLACE_HOLD_SOURCE, {
      type: 'vector',
      url: 'mapbox://mapbox.mapbox-streets-v8',
      maxzoom: PLACE_TILE_ZOOM,
    });
  }
  if (map.getLayer(PLACE_HOLD_LAYER)) {
    map.setFilter(PLACE_HOLD_LAYER, filter);
    return true;
  }
  map.addLayer({
    id: PLACE_HOLD_LAYER,
    type: 'symbol',
    source: PLACE_HOLD_SOURCE,
    'source-layer': 'poi_label',
    minzoom: PLACE_TILE_ZOOM + 1,
    filter,
    layout: {
      'text-field': ['coalesce', ['get', 'name_en'], ['get', 'name']],
      'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
      'text-size': 13,
      'text-max-width': 12,
      'text-anchor': 'center',
      'text-allow-overlap': true,
      'text-ignore-placement': true,
    },
    paint: {
      'text-color': '#202124',
      'text-halo-color': '#f7f7f8',
      'text-halo-width': 1.2,
    },
  });
  return true;
}
